import { useEffect, useState } from "react";
import { Film, Plus, X } from "lucide-react";
import { api, API } from "@/lib/api";

const MAX_MB = 80;
const CHUNK = 4 * 1024 * 1024;
const STEPS = [
  ["01", "Introduce yourself", "Name, age, height and city, looking straight at the camera"],
  ["02", "Turn around slowly", "Front, profile, back and other profile, full length"],
  ["03", "Walk", "A few steps towards the camera and back, natural pace"],
];

export const DEFAULT_EXAMPLE = "/media/example-video.mp4";

export const useExampleVideo = () => {
  const [src, setSrc] = useState(DEFAULT_EXAMPLE);
  const [custom, setCustom] = useState(false);
  const refresh = () => api.get("/site/example-video/info").then((r) => {
    setCustom(!!r.data?.custom);
    setSrc(r.data?.custom ? `${API}/site/example-video?v=${encodeURIComponent(r.data.updated_at || "")}` : DEFAULT_EXAMPLE);
  }).catch(() => setSrc(DEFAULT_EXAMPLE));
  useEffect(() => { refresh(); }, []);
  return { src, custom, refresh };
};

const ExampleClip = () => {
  const { src } = useExampleVideo();
  return (
    <video key={src} src={src} controls muted playsInline loop preload="metadata" data-testid="video-example-player"
      className="aspect-[9/16] w-28 shrink-0 bg-[#1c1418] object-cover sm:w-32" />
  );
};

export async function uploadVideo(id, token, file, onProgress) {
  const total = Math.ceil(file.size / CHUNK);
  for (let i = 0; i < total; i += 1) {
    const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
    await api.post(`/applications/${id}/video?index=${i}&total=${total}&mime=${encodeURIComponent(file.type || "video/mp4")}`, blob,
      { headers: { "Content-Type": "application/octet-stream", "X-Upload-Token": token } });
    onProgress(Math.round(((i + 1) / total) * 100));
  }
}

export const VideoPicker = ({ video, setVideo, progress }) => {
  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) return alert("Please choose a video file.");
    if (file.size > MAX_MB * 1024 * 1024) return alert(`The video must be smaller than ${MAX_MB} MB.`);
    setVideo(file);
  };

  return (
    <div data-testid="video-picker">
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1c1418]/70">Presentation video (max 1 min, {MAX_MB} MB)</span>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div data-testid="video-example" className="bg-[#f4f4f4] p-4">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em]"><Film className="h-4 w-4 text-[#e9458f]" /> Example · what to film</p>
          <div className="mt-3 flex gap-4">
            <ExampleClip />
            <ol className="space-y-2">
              {STEPS.map(([n, t, d]) => (
                <li key={n} className="flex gap-3 text-sm"><span className="font-display text-xl leading-none">{n}</span><span><b>{t}</b><br /><span className="text-[#1c1418]/70">{d}</span></span></li>
              ))}
            </ol>
          </div>
          <p className="mt-3 text-xs text-[#1c1418]/70">Vertical phone video, daylight, plain wall, no music or filters.</p>
        </div>
        {video ? (
          <div className="relative flex flex-col justify-between border border-[#1c1418] p-4" data-testid="video-selected">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em]">Selected video</p>
            <p className="mt-2 break-all text-sm">{video.name} · {(video.size / 1048576).toFixed(1)} MB</p>
            {progress !== null && <div className="mt-3 h-1 bg-[#1c1418]/10"><div className="h-1 bg-[#1c1418]" style={{ width: `${progress}%` }} /></div>}
            <button type="button" onClick={() => setVideo(null)} data-testid="video-remove" className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-[#1c1418] text-white"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <label data-testid="video-upload-slot" className="flex min-h-[160px] cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-[#1c1418]/30 text-center hover:border-[#1c1418]">
            <Plus className="h-6 w-6 text-[#e9458f]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Add your video</span>
            <input type="file" accept="video/*" onChange={pick} className="hidden" data-testid="video-input" />
          </label>
        )}
      </div>
    </div>
  );
};
